import { CrossIcon } from "@solomon-labs/design/components/icons";
import { Text } from "@solomon-labs/design/components/ui";
import React, { useState } from "react";

import type {
    ClaimLinkDelivery,
    ClaimLinkResponse
} from "../../types/orgAccessContracts";
import { copyText } from "../../utils/copyText";
import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";

export type ClaimLinkKind =
    | "invitation"
    | "client-invitation"
    | "password-reset";

export interface ClaimLinkDialogState {
    url: string;
    kind: ClaimLinkKind;
    delivery: ClaimLinkDelivery;
}

export interface ClaimLinkDialogCopy {
    title: string;
    instruction: string;
}

/**
 * What a `client-invitation` calls the invited organization. Consoles name
 * that role differently (the YaaS Distributor Console says Company
 * Recipient), so the wording is passed in; the default keeps "Client".
 */
export interface ClaimLinkClientTerm {
    /** Title Case, for the dialog title (e.g. "Client"). */
    title: string;
    /** Lowercase, for mid-sentence use (e.g. "client"). */
    noun: string;
}

const DEFAULT_CLIENT_TERM: ClaimLinkClientTerm = {
    title: "Client",
    noun: "client"
};

function claimLinkTitle(
    kind: ClaimLinkKind,
    clientTerm: ClaimLinkClientTerm
): string {
    switch (kind) {
        case "invitation":
            return "Invitation Link";
        case "client-invitation":
            return `${clientTerm.title} Invitation Link`;
        case "password-reset":
            return "Password Reset Link";
    }
}

export function createClaimLinkDialogState(
    response: ClaimLinkResponse,
    kind: ClaimLinkKind
): ClaimLinkDialogState {
    return {
        url: response.claimUrl,
        kind,
        delivery: response.delivery
    };
}

export function getClaimLinkDialogCopy(
    kind: ClaimLinkKind,
    delivery: ClaimLinkDelivery,
    subjectName?: string | null,
    clientTerm: ClaimLinkClientTerm = DEFAULT_CLIENT_TERM
): ClaimLinkDialogCopy {
    const recipient =
        kind === "client-invitation"
            ? `${subjectName ?? `the ${clientTerm.noun}`}'s administrator`
            : kind === "password-reset"
              ? "the team member"
              : "the invited team member";
    const action =
        kind === "password-reset"
            ? "reset their password"
            : "claim their account";
    const instruction =
        delivery === "email_and_url"
            ? `An email was sent to ${recipient}. You can also copy this link so they can ${action}.`
            : `Email delivery was unavailable. Copy this link and send it to ${recipient} so they can ${action}.`;

    return { title: claimLinkTitle(kind, clientTerm), instruction };
}

interface ClaimLinkDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    url: string | null;
    delivery: ClaimLinkDelivery;
    kind?: ClaimLinkKind;
    subjectName?: string | null;
    /** Wording for a `client-invitation`; defaults to "Client". */
    clientTerm?: ClaimLinkClientTerm;
}

export function ClaimLinkDialog({
    open,
    onOpenChange,
    url,
    delivery,
    kind = "invitation",
    subjectName,
    clientTerm
}: ClaimLinkDialogProps): React.ReactElement | null {
    const [copied, setCopied] = useState(false);

    if (!url) return null;

    const copy = getClaimLinkDialogCopy(
        kind,
        delivery,
        subjectName,
        clientTerm
    );

    const handleCopy = async (): Promise<void> => {
        const result = await copyText(url);
        if (result === "clipboard") {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next) onOpenChange(false);
            }}
        >
            <DialogContent className="max-h-[88vh] max-w-[520px] gap-0 overflow-hidden p-0">
                <div className="border-b border-line-base px-6 pt-6 pb-4">
                    <div className="flex items-center justify-between gap-4">
                        <Text
                            variant="h2"
                            render={<h2 />}
                            className="text-foreground-base"
                        >
                            {copy.title}
                        </Text>
                        <button
                            type="button"
                            aria-label="Close"
                            onClick={() => onOpenChange(false)}
                            className="-mr-1 shrink-0 p-1 text-foreground-imprint hover:text-foreground-base"
                        >
                            <CrossIcon className="size-6" />
                        </button>
                    </div>
                </div>

                <div className="flex flex-col gap-4 overflow-y-auto px-6 py-5">
                    <Text
                        variant="small"
                        render={<p />}
                        className="text-foreground-base leading-snug"
                    >
                        {copy.instruction}
                    </Text>
                    <Text
                        variant="small"
                        render={<code />}
                        className="block select-all break-all border border-line-base bg-surface-sunken p-3 font-numeric text-foreground-neutral"
                    >
                        {url}
                    </Text>
                </div>

                <div className="px-6 pt-8 pb-5">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="raised"
                            interaction="lifted"
                            size="md"
                            className="min-w-28 justify-center"
                            onClick={() => void handleCopy()}
                        >
                            {copied ? "Copied" : "Copy Link"}
                        </Button>
                        <Button
                            variant="transparent"
                            size="md"
                            className="underline"
                            onClick={() => onOpenChange(false)}
                        >
                            Close
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
